import type { Maybe } from '@adornis/base/utilTypes.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import {
  getInterviewInvitationList,
  getMatchingScoreCorrelation,
  getMatchingWeekJobApplicationStats,
  getMatchingWeekStatistics,
} from '../../../api/statistics.js';
import { adminRouter } from './admin-routes.js';

const formatDateInput = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// The chosen period survives navigating away and reloads; default is the last 30 days.
const RANGE_STORAGE_KEY = 'mw-admin-statistics-date-range';

const isValidDateInput = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

const readStoredRange = (): { from?: string; to?: string } => {
  try {
    const stored = JSON.parse(localStorage.getItem(RANGE_STORAGE_KEY) ?? '{}');
    return {
      from: isValidDateInput(stored.from) ? stored.from : undefined,
      to: isValidDateInput(stored.to) ? stored.to : undefined,
    };
  } catch {
    return {};
  }
};

const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return formatDateInput(d);
};

export const AdminStatistics = stateful(() => {
  const storedRange = readStoredRange();
  const [from, setFrom] = useState<string>(storedRange.from ?? defaultFrom());
  const [to, setTo] = useState<string>(storedRange.to ?? formatDateInput(new Date()));

  const persistRange = (nextFrom: string, nextTo: string) => {
    try {
      localStorage.setItem(RANGE_STORAGE_KEY, JSON.stringify({ from: nextFrom, to: nextTo }));
    } catch {
      // storage full/blocked — the page still works, just without persistence
    }
  };
  const updateFrom = (value: string) => {
    setFrom(value);
    persistRange(value, to);
  };
  const updateTo = (value: string) => {
    setTo(value);
    persistRange(from, value);
  };

  const rangeValid = !!from && !!to && from <= to;
  // The selected "to" day is included in the period.
  const fromDate = () => new Date(`${from}T00:00:00`);
  const toDate = () => new Date(`${to}T23:59:59.999`);

  const stats = useQuery(
    () =>
      rangeValid
        ? getMatchingWeekStatistics(fromDate(), toDate())({
            activeJobPositions: 1,
            companiesWithActiveJobPositions: 1,
            newUsers: 1,
            applications: 1,
            interviewInvitations: 1,
            interviewsAgreed: 1,
            interviewsHeld: 1,
            interviewsNotAttended: 1,
          })
        : Promise.resolve(null),
    [from, to],
  );

  const jobStats = useQuery(
    () =>
      rangeValid
        ? getMatchingWeekJobApplicationStats(fromDate(), toDate())({
            jobPositionID: 1,
            companyID: 1,
            jobTitle: 1,
            companyName: 1,
            applications: 1,
            invitations: 1,
            interviewsAgreed: 1,
            interviewsHeld: 1,
          })
        : Promise.resolve(null),
    [from, to],
  );

  const invitations = useQuery(
    () =>
      rangeValid
        ? getInterviewInvitationList(fromDate(), toDate())({
            applicationID: 1,
            userID: 1,
            jobPositionID: 1,
            companyID: 1,
            firstName: 1,
            lastName: 1,
            email: 1,
            jobTitle: 1,
            companyName: 1,
            status: 1,
            interviewStart: 1,
            interviewAttended: 1,
            matchingScore: 1,
          })
        : Promise.resolve(null),
    [from, to],
  );

  const correlation = useQuery(
    () =>
      rangeValid
        ? getMatchingScoreCorrelation(fromDate(), toDate())({
            scoredApplications: 1,
            unscoredApplications: 1,
            avgScoreInvited: 1,
            avgScoreNotInvited: 1,
            correlation: 1,
            buckets: { label: 1, applications: 1, invitations: 1 },
          })
        : Promise.resolve(null),
    [from, to],
  );

  const statusLabel = (status: Maybe<string>) =>
    status === 'BOOKED' ? m.application_status_BOOKED() : m.application_status_ACCEPTED();

  const attendanceLabel = (attended: Maybe<boolean>) =>
    attended === true
      ? m.admin_statistics_attendance_yes()
      : attended === false
        ? m.admin_statistics_attendance_no()
        : m.admin_statistics_attendance_pending();

  // Pins the actions column to the right edge so the goto buttons stay visible while the
  // (wide) tables scroll horizontally. The background must match the row: table-zebra shades
  // every 2nd body row (0-based odd index) with base-200; header and odd rows are base-100.
  const stickyCol = (rowIndex?: number) => {
    const bg = rowIndex !== undefined && rowIndex % 2 === 1 ? 'bg-base-200' : 'bg-base-100';
    return `sticky right-0 z-10 border-l border-base-300 ${bg}`;
  };

  // quick-navigation icons per row; a button only renders when its target ID is present
  const gotoButtons = (opts: { userID?: Maybe<string>; jobPositionID?: Maybe<string>; companyID?: Maybe<string> }) => html`
    <div class="flex justify-end gap-1">
      ${opts.userID
        ? html`
            <a
              class="btn btn-ghost btn-xs btn-square"
              title=${m.admin_statistics_goto_user()}
              href=${adminRouter.link('userDetail', { params: { userID: opts.userID } })}
            >
              <span class="icon-[material-symbols--person]"></span>
            </a>
          `
        : nothing}
      ${opts.jobPositionID && opts.companyID
        ? html`
            <a
              class="btn btn-ghost btn-xs btn-square"
              title=${m.admin_statistics_goto_job()}
              href=${adminRouter.link('jobDetail', { params: { companyID: opts.companyID } }) + '/' + opts.jobPositionID}
            >
              <span class="icon-[material-symbols--work]"></span>
            </a>
          `
        : nothing}
      ${opts.companyID
        ? html`
            <a
              class="btn btn-ghost btn-xs btn-square"
              title=${m.admin_statistics_goto_company()}
              href=${adminRouter.link('companyDetail', { params: { companyID: opts.companyID } })}
            >
              <span class="icon-[material-symbols--domain]"></span>
            </a>
          `
        : nothing}
    </div>
  `;

  const exportInvitationsCSV = () => {
    const rows = invitations.value ?? [];
    const header = [
      m.admin_statistics_name_column(),
      m.admin_statistics_email_column(),
      m.admin_statistics_company_column(),
      m.admin_statistics_job_column(),
      m.admin_statistics_status_column(),
      m.admin_statistics_interview_date_column(),
      m.admin_statistics_attendance_column(),
      m.admin_statistics_score_column(),
    ];
    const csvRows = rows.map(r => [
      `${r.lastName ?? ''}, ${r.firstName ?? ''}`,
      r.email ?? '',
      r.companyName ?? '',
      r.jobTitle ?? '',
      statusLabel(r.status),
      r.interviewStart ? format(new Date(r.interviewStart), 'dd.MM.yyyy HH:mm') : '',
      attendanceLabel(r.interviewAttended),
      r.matchingScore ?? '',
    ]);
    const csv = [header, ...csvRows]
      .map(cols => cols.map(v => `"${String(v ?? '').replaceAll('"', '""')}"`).join(';'))
      .join('\n');
    // BOM so Excel opens umlauts correctly
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gespraechseinladungen_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statCard = (label: string, value: Maybe<number>) => html`
    <div class="card bg-base-100 shadow-xl">
      <div class="card-body">
        <div class="text-sm text-base-content/70">${label}</div>
        <div class="text-4xl font-bold text-base-content">
          ${stats.loading ? html`<span class="loading loading-spinner"></span>` : (value ?? 0)}
        </div>
      </div>
    </div>
  `;

  const sectionLoading = html`<div class="p-8 flex justify-center">
    <span class="loading loading-spinner loading-lg"></span>
  </div>`;

  const perJobSection = html`
    <div class="card bg-base-100 shadow-xl">
      <div class="card-body">
        <h2 class="card-title">${m.admin_statistics_per_job_title()}</h2>
        ${jobStats.loading
          ? sectionLoading
          : html`
              <div class="overflow-x-auto">
                <table class="table table-zebra table-sm">
                  <thead>
                    <tr>
                      <th>${m.admin_statistics_job_column()}</th>
                      <th>${m.admin_statistics_company_column()}</th>
                      <th class="text-right">${m.admin_statistics_applications_column()}</th>
                      <th class="text-right">${m.admin_statistics_invitations_column()}</th>
                      <th class="text-right">${m.admin_statistics_agreed_column()}</th>
                      <th class="text-right">${m.admin_statistics_interviews_column()}</th>
                      <th class=${stickyCol()}></th>
                    </tr>
                  </thead>
                  <tbody>
                    ${(jobStats.value ?? []).map(
                      (row, i) => html`
                        <tr>
                          <td>${row.jobTitle ?? ''}</td>
                          <td>${row.companyName ?? ''}</td>
                          <td class="text-right">${row.applications ?? 0}</td>
                          <td class="text-right">${row.invitations ?? 0}</td>
                          <td class="text-right">${row.interviewsAgreed ?? 0}</td>
                          <td class="text-right">${row.interviewsHeld ?? 0}</td>
                          <td class=${stickyCol(i)}>
                            ${gotoButtons({ jobPositionID: row.jobPositionID, companyID: row.companyID })}
                          </td>
                        </tr>
                      `,
                    )}
                  </tbody>
                </table>
              </div>
            `}
      </div>
    </div>
  `;

  const invitedSection = html`
    <div class="card bg-base-100 shadow-xl">
      <div class="card-body">
        <div class="flex flex-wrap justify-between items-center gap-2">
          <h2 class="card-title">
            ${m.admin_statistics_invited_title()}
            ${!invitations.loading ? html`<span class="badge badge-neutral">${invitations.value?.length ?? 0}</span>` : nothing}
          </h2>
          <button
            class="btn btn-outline btn-sm"
            ?disabled=${invitations.loading || !(invitations.value ?? []).length}
            @click=${exportInvitationsCSV}
          >
            <span class="icon-[material-symbols--download]"></span>
            ${m.admin_statistics_export_csv()}
          </button>
        </div>
        ${invitations.loading
          ? sectionLoading
          : !(invitations.value ?? []).length
            ? html`<p class="text-base-content/70">${m.admin_statistics_no_invitations()}</p>`
            : html`
                <div class="overflow-x-auto">
                  <table class="table table-zebra table-sm">
                    <thead>
                      <tr>
                        <th>${m.admin_statistics_name_column()}</th>
                        <th>${m.admin_statistics_email_column()}</th>
                        <th>${m.admin_statistics_company_column()}</th>
                        <th>${m.admin_statistics_job_column()}</th>
                        <th>${m.admin_statistics_status_column()}</th>
                        <th>${m.admin_statistics_interview_date_column()}</th>
                        <th>${m.admin_statistics_attendance_column()}</th>
                        <th class="text-right">${m.admin_statistics_score_column()}</th>
                        <th class=${stickyCol()}></th>
                      </tr>
                    </thead>
                    <tbody>
                      ${(invitations.value ?? []).map(
                        (row, i) => html`
                          <tr>
                            <td>${row.lastName ?? ''}, ${row.firstName ?? ''}</td>
                            <td>${row.email ?? ''}</td>
                            <td>${row.companyName ?? ''}</td>
                            <td>${row.jobTitle ?? ''}</td>
                            <td>
                              <span class="badge ${row.status === 'BOOKED' ? 'badge-info' : 'badge-success'}">
                                ${statusLabel(row.status)}
                              </span>
                            </td>
                            <td>
                              ${row.interviewStart ? format(new Date(row.interviewStart), 'dd.MM.yyyy HH:mm') : '–'}
                            </td>
                            <td>
                              <span
                                class="badge ${row.interviewAttended === true
                                  ? 'badge-success'
                                  : row.interviewAttended === false
                                    ? 'badge-error'
                                    : 'badge-ghost'}"
                              >
                                ${attendanceLabel(row.interviewAttended)}
                              </span>
                            </td>
                            <td class="text-right">${row.matchingScore ?? '–'}</td>
                            <td class=${stickyCol(i)}>
                              ${gotoButtons({
                                userID: row.userID,
                                jobPositionID: row.jobPositionID,
                                companyID: row.companyID,
                              })}
                            </td>
                          </tr>
                        `,
                      )}
                    </tbody>
                  </table>
                </div>
              `}
      </div>
    </div>
  `;

  const correlationSection = () => {
    const corr = correlation.value;
    const rate = (invited: Maybe<number>, total: Maybe<number>) =>
      total ? `${Math.round(((invited ?? 0) / total) * 100)}%` : '–';
    return html`
      <div class="card bg-base-100 shadow-xl">
        <div class="card-body">
          <h2 class="card-title">${m.admin_statistics_correlation_title()}</h2>
          ${correlation.loading
            ? sectionLoading
            : !corr || !corr.scoredApplications
              ? html`<p class="text-base-content/70">${m.admin_statistics_correlation_not_available()}</p>`
              : html`
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="stat bg-base-200 rounded-box">
                      <div class="stat-title">${m.admin_statistics_avg_score_invited()}</div>
                      <div class="stat-value text-2xl">
                        ${corr.avgScoreInvited != null ? corr.avgScoreInvited.toFixed(1) : '–'}
                      </div>
                    </div>
                    <div class="stat bg-base-200 rounded-box">
                      <div class="stat-title">${m.admin_statistics_avg_score_not_invited()}</div>
                      <div class="stat-value text-2xl">
                        ${corr.avgScoreNotInvited != null ? corr.avgScoreNotInvited.toFixed(1) : '–'}
                      </div>
                    </div>
                    <div class="stat bg-base-200 rounded-box">
                      <div class="stat-title">${m.admin_statistics_correlation_coefficient()}</div>
                      <div class="stat-value text-2xl">
                        ${corr.correlation != null ? corr.correlation.toFixed(2) : '–'}
                      </div>
                    </div>
                  </div>
                  <div class="overflow-x-auto mt-4">
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th>${m.admin_statistics_score_bucket_column()}</th>
                          <th class="text-right">${m.admin_statistics_applications_column()}</th>
                          <th class="text-right">${m.admin_statistics_invitations_column()}</th>
                          <th class="text-right">${m.admin_statistics_invitation_rate_column()}</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${(corr.buckets ?? []).map(
                          bucket => html`
                            <tr>
                              <td>${bucket.label}</td>
                              <td class="text-right">${bucket.applications ?? 0}</td>
                              <td class="text-right">${bucket.invitations ?? 0}</td>
                              <td class="text-right">${rate(bucket.invitations, bucket.applications)}</td>
                            </tr>
                          `,
                        )}
                      </tbody>
                    </table>
                  </div>
                  ${corr.unscoredApplications
                    ? html`<p class="text-sm text-base-content/60 mt-2">
                        ${m.admin_statistics_unscored_hint({ count: corr.unscoredApplications })}
                      </p>`
                    : nothing}
                `}
        </div>
      </div>
    `;
  };

  return html`
    <div>
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-3xl font-bold text-base-content">${m.admin_statistics_title()}</h1>
      </div>

      <div class="flex flex-wrap items-end gap-3 mb-6">
        <div class="flex flex-col gap-1">
          <span class="text-sm text-base-content/70">${m.admin_statistics_from_label()}</span>
          <input
            type="date"
            class="input input-bordered"
            .value=${from}
            @change=${(e: Event) => updateFrom((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-base-content/70">${m.admin_statistics_to_label()}</span>
          <input
            type="date"
            class="input input-bordered"
            .value=${to}
            @change=${(e: Event) => updateTo((e.target as HTMLInputElement).value)}
          />
        </div>
      </div>

      ${rangeValid
        ? html`
            <div class="flex flex-col gap-6">
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                ${statCard(m.admin_statistics_active_jobs(), stats.value?.activeJobPositions)}
                ${statCard(
                  m.admin_statistics_companies_with_active_jobs(),
                  stats.value?.companiesWithActiveJobPositions,
                )}
                ${statCard(m.admin_statistics_new_users(), stats.value?.newUsers)}
                ${statCard(m.admin_statistics_applications(), stats.value?.applications)}
                ${statCard(m.admin_statistics_interview_invitations(), stats.value?.interviewInvitations)}
                ${statCard(m.admin_statistics_interviews_agreed(), stats.value?.interviewsAgreed)}
                ${statCard(m.admin_statistics_interviews_held(), stats.value?.interviewsHeld)}
                ${statCard(m.admin_statistics_interviews_not_attended(), stats.value?.interviewsNotAttended)}
              </div>
              ${perJobSection} ${invitedSection} ${correlationSection()}
            </div>
          `
        : html`<div class="alert alert-warning">${m.admin_statistics_invalid_range()}</div>`}
    </div>
  `;
});
